import { useSelector } from "react-redux";

import { WaterFlowPath, WaterflowLayer } from "@nens/deck-gl";
import { getDistanceInM } from "@nens/shared-utils";

import { getFlowSpeedLayer } from "../../state/layerCalculationMenu";
import { LayerType, PIPE_TYPES, getLayers } from "../../state/layerOneDMenu";
import { getZoom } from "../../state/map";
import { getSimulationStatus } from "../../state/simulation";
import { Asset } from "../../types/assets";

import { getCalculationLayersColors } from "./DeckGlConsts";
import useTimeAnimation from "./helpers/useTimeAnimation";
import useAppliedFlows from "./OneDFlow/useAppliedFlows";
import useOneDFlows from "./OneDFlow/useOneDFlows";

const speedMultiplier = 100;

const useWaterflowProps = (assets?: { [value: string]: Asset[] }) => {
  const flowLayer = useSelector(getFlowSpeedLayer);
  const flows = useOneDFlows(assets);
  const zoom = useSelector(getZoom) || 0;

  const simulationRunning = useSelector(getSimulationStatus).status === "running";
  const oneDLayersStyle = useSelector(getLayers);
  const COLORS = getCalculationLayersColors({ waterflows: flowLayer });
  const { timeAnimation, refrenceTimeAnimationProgress } = useTimeAnimation();
  const { appliedFlows, lastAppliedUpdateTime } = useAppliedFlows(
    flows,
    refrenceTimeAnimationProgress,
    speedMultiplier * flowLayer.speedPercentage
  );

  const flowTreshold =
    (flowLayer.waterflowThreshold * speedMultiplier * flowLayer.speedPercentage) / 50;
  const flowHighlightTreshold =
    (flowLayer.waterflowHighlightThreshold * speedMultiplier * flowLayer.speedPercentage) / 50;

  return new WaterflowLayer({
    id: "waterflow-layer",
    data: appliedFlows,
    jointRounded: true,
    opacity: 1,
    widthMinPixels: zoom / 4,
    latestUpdateTime: lastAppliedUpdateTime,
    simulationRunning: simulationRunning,
    color: COLORS.WATERFLOWS,
    flowTreshold: flowTreshold,
    flowHighlightTreshold: flowHighlightTreshold,
    visible: flowLayer.visible,
    // Trail length is not based on actual length in pixels but in time. So slower speed = shorter trail && higher speed = longer trail
    // We calculate the average speed of the flow and apply that to every coordinate that is supposed to be visible
    // This is the only way to create a smooth transition from 1 speed to another
    getTrailLength: (d: WaterFlowPath[]) => {
      const speedArray = d.flatMap((p: WaterFlowPath) => p.coordinates.flatMap(() => p.speed));
      // If simulation is running make all trails a length of 60 so they don't jump when speed changes
      const average = simulationRunning
        ? 1
        : Math.abs(speedArray.reduce((a, b) => a + b, 0) / speedArray.length);
      return d.flatMap((p: WaterFlowPath) =>
        p.coordinates.flatMap(() =>
          oneDLayersStyle[p.modelType as LayerType].visible &&
          (p.modelType !== "pipes" ||
            (p.pipeType !== undefined &&
              oneDLayersStyle.pipes.pipeStyles[PIPE_TYPES[p.pipeType]] &&
              oneDLayersStyle.pipes.pipeStyles[PIPE_TYPES[p.pipeType]].visible)) &&
          average
            ? 60 / average
            : 0
        )
      );
    },
    // We don't actually just pass the speed of a path to the waterflow. We change the timestamp at which it has to be at the next coordinate.
    getTimestamps: (d: WaterFlowPath[]) => {
      let distance = 0;
      return d.flatMap((p: WaterFlowPath, pathIndex) =>
        p.coordinates.flatMap((coordinate, coordIndex) => {
          const lastCoords = p.coordinates[coordIndex - 1];
          const lastPath = d[pathIndex - 1];
          // Get distance from last coordinate
          if (lastCoords) {
            const ownSpeed =
              !simulationRunning && Math.abs(p.speed) > flowTreshold ? Math.abs(p.speed) : 1;
            distance += getDistanceInM(coordinate, lastCoords) / ownSpeed;
          }
          // Get distance from last path if current coordinate is the first of the path
          else if (lastPath) {
            const lastPathSpeed =
              !simulationRunning && Math.abs(lastPath.speed) > flowTreshold
                ? Math.abs(lastPath.speed)
                : 1;
            const lastPathCoords = lastPath.coordinates;
            distance +=
              getDistanceInM(coordinate, lastPathCoords[lastPathCoords.length - 1]) / lastPathSpeed;
          }
          return distance;
        })
      );
    },
    ...timeAnimation,
    updateTriggers: {
      getTrailLength: oneDLayersStyle,
    },
  });
};

export default useWaterflowProps;
